package com.milta.feature.optimizationquota.data.remote

import com.milta.core.network.ApiException
import com.milta.core.network.Endpoints
import com.milta.core.network.baseUrl
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.optimizationquota.data.remote.dto.AdminAccountOptimizationQuotaDto
import com.milta.feature.optimizationquota.data.remote.dto.OptimizationHistoryEntryDto
import com.milta.feature.optimizationquota.data.remote.dto.OptimizationLimitsDto
import com.milta.feature.optimizationquota.data.remote.dto.OptimizationQuotaDto
import com.milta.feature.optimizationquota.data.remote.dto.ReplaceRemainingOptimizationsRequest
import com.milta.feature.route.data.remote.dto.PagedResultDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

/** MIL-97 — RoutaR optimization counter: the caller's own (mobile + web) and the Admin views. */
class OptimizationQuotaApi(private val client: HttpClient) {

    /** GET /api/v1/optimization-quota/me */
    suspend fun getMyQuota(): OptimizationQuotaDto =
        client.get("$baseUrl/${Endpoints.optimizationQuotaMe}").decodeOrThrow()

    /** GET /api/v1/optimization-quota/me/history — most recent first. */
    suspend fun getMyHistory(page: Int, pageSize: Int): PagedResultDto<OptimizationHistoryEntryDto> =
        client.get("$baseUrl/${Endpoints.optimizationQuotaMeHistory}") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** GET /api/v1/admin/usage-limits/optimization */
    suspend fun getLimits(): OptimizationLimitsDto =
        client.get("$baseUrl/${Endpoints.adminOptimizationLimits}").decodeOrThrow()

    /** PUT /api/v1/admin/usage-limits/optimization — any value ≤ 0 is a 400. */
    suspend fun updateLimits(limits: OptimizationLimitsDto): OptimizationLimitsDto =
        client.put("$baseUrl/${Endpoints.adminOptimizationLimits}") {
            contentType(ContentType.Application.Json)
            setBody(limits)
        }.decodeOrThrow()

    /** GET /api/v1/admin/accounts/{accountId}/optimization-quota */
    suspend fun getAccountQuota(accountId: String): AdminAccountOptimizationQuotaDto =
        client.get("$baseUrl/${Endpoints.adminAccountOptimizationQuota(accountId)}").decodeOrThrow()

    /** GET /api/v1/admin/accounts/{accountId}/optimization-quota/history */
    suspend fun getAccountHistory(accountId: String, page: Int, pageSize: Int): PagedResultDto<OptimizationHistoryEntryDto> =
        client.get("$baseUrl/${Endpoints.adminAccountOptimizationHistory(accountId)}") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }.decodeOrThrow()

    /** PUT /api/v1/admin/accounts/{accountId}/optimization-quota/remaining — returns the updated counter. */
    suspend fun replaceRemaining(accountId: String, remaining: Int): OptimizationQuotaDto =
        client.put("$baseUrl/${Endpoints.adminAccountOptimizationRemaining(accountId)}") {
            contentType(ContentType.Application.Json)
            setBody(ReplaceRemainingOptimizationsRequest(remaining))
        }.decodeOrThrow()

    private suspend inline fun <reified T> HttpResponse.decodeOrThrow(): T {
        if (!status.isSuccess()) {
            val apiError = runCatching { body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${status.value}"
            throw ApiException(status.value, message)
        }
        return body()
    }
}
